import { User, BaseUser } from "@/types";
import dayjs from "dayjs";
import _ from "lodash";
import { formatNumber as fN } from "@/uiCore";
import duration from "dayjs/plugin/duration";
import { Datastore } from "@/pages/datastores/types";
import { DbtModelFiltersResponse } from "@/types/dbtModels";
import { MOBILE_SCREEN_BREAKPOINT } from "@/helpers/constants";
import { SortDirection } from "@/components/tables/types";

dayjs.extend(duration);

export const getTrueKeys = (x: unknown[]) => {
  const result: unknown[] = [];
  x.forEach((v, k) => {
    if (!v) return;
    if (Array.isArray(k)) result.push(...k);
    else result.push(k);
  });
  return result;
};

export const isEmpty = (arr?: unknown[]) => !arr || !Array.isArray(arr) || arr.length === 0;

// add proper types for these functions
export const toggleSetValue = (s: Set<string>, key: string) => (s.delete(key) ? s : s.add(key));

export const getScopeString = (scope: string[]) => scope.join("/");
export const getFullyQualifiedName = (scope: string[]) => scope?.slice(1).join(".") || "";

export const isStrDateOlderThan = (dateStr: string, numDays: number) =>
  dayjs().diff(dayjs(dateStr, "DD-MMM-YY HH:mm"), "day") > numDays;

export const containsScope = (scopeArr: string[][], scope: string[]) =>
  scopeArr.length === 0 ? false : !!scopeArr.find(item => _.isEqual(item, scope));

export const downloadBlob = (blob: Blob) => {
  var url = window.URL.createObjectURL(blob);
  var a = document.createElement("a");
  a.href = url;
  document.body.appendChild(a); // append the element to the dom
  a.click();
  a.remove(); // afterwards, remove the element
};

/**
 * Downloads a file from a given URL by creating a temporary anchor element.
 *
 * Note: The previous `filename` parameter was removed because:
 * 1. It was never used anywhere in the codebase
 * 2. The `link.download` attribute was removed since we're using S3 signed URLs
 *    which already include the correct filename in the Content-Disposition header
 * 3. Opening in a new tab (_blank) allows the browser to handle the download
 *    with the filename provided by the server
 */

export function downloadFileFromUrl(url: string) {
  const link = document.createElement("a");
  link.href = url;
  // Open in new tab - browser will download using filename from server's Content-Disposition header
  link.target = "_blank";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export const onSortChange =
  (key: string, setSort: Function, setSortAttribute: Function) => (newOrder: SortDirection) => {
    setSort((prevSortOrder: Record<string, SortDirection>) => ({
      ...prevSortOrder,
      [key]: newOrder,
    }));
    setSortAttribute(key);
  };

export const roundToTwoDecimalPlaces = (number: number) => {
  return Math.round(number * 100) / 100;
};

export const getUserById = <T extends Pick<User, "id">>(
  users: T[] | undefined,
  id: T["id"]
): T | undefined => users?.find(u => u.id === id);
export const getUserByUuid = <T extends Pick<User, "uuid">>(
  users: T[] | undefined,
  uid: T["uuid"]
): T | undefined => users?.find(u => u.uuid === uid);

/**
 * Determines if a user should be considered deleted (soft delete)
 * Currently checks if all 4 flags are false: is_active, is_verified, is_invited, is_onboarded
 * This function can be easily modified in the future if deletion logic changes
 */
export const isUserDeleted = (user: BaseUser): boolean => {
  if (!user) return false;

  // User is deleted if deleted_at has a valid timestamp (not null, undefined, or empty string)
  return user.deleted_at !== null && user.deleted_at !== undefined && user.deleted_at !== "";
};

export function formatNumber(n: string, wholeNumber = false) {
  const number = parseFloat(n);

  const divisors = {
    "1000": "",
    "1000000": "K",
    "1000000000": "M",
    "1000000000000": "B",
  };
  let divisor = 1000000000000;
  let suffix = "B";

  for (let l in divisors) {
    const limit = Number(l);
    if (number < Number(limit)) {
      divisor = limit / 1000;
      suffix = divisors[l as keyof typeof divisors];
      break;
    }
  }

  let formattedNumber;
  if (1000000 <= number && number < 1000000000) {
    formattedNumber = (number / 1000000).toFixed(1) + "M";
  } else {
    formattedNumber =
      number >= 10 || wholeNumber
        ? (Math.ceil((number / divisor) * 10) / 10 + suffix).replace(".0", "")
        : number.toFixed(2);
  }

  return formattedNumber;
}

export const formatDate = (date: string) => {
  if (!date) return "None";
  if (date.includes("N/A")) return "N/A";

  const parsed = dayjs(date);
  if (!parsed.isValid()) return "None";

  const formatted = parsed.format("DD MMM, YYYY");
  return date.includes("<") ? `Before ${formatted}` : formatted;
};

export const formatCost = (cost: string | number, currency: string): string => {
  try {
    // @ts-ignore
    if (isNaN(parseFloat(cost))) {
      return cost?.toString();
    }
    return fN(cost, { currency });
  } catch {
    return cost?.toString();
  }
};

export function formatBytes(bytes: number, decimals = 2): string {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
}

// Hash function that converts string to consistent number
export const stringToNumber = (str: string, max: number) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash) % max;
};

/**
 * Format seconds to a compact human-readable duration string.
 *
 * @example
 * formatSeconds(1800) => "30m"
 * formatSeconds(5400) => "1.5h"
 * formatSeconds(172800) => "2.0d"
 */
export const formatSeconds = (seconds: number | null | undefined): string => {
  if (seconds === null || seconds === undefined) return "-";
  const hours = seconds / 3600;
  if (hours < 1) return `${Math.round(seconds / 60)}m`;
  if (hours < 24) return `${hours.toFixed(1)}h`;
  return `${(hours / 24).toFixed(1)}d`;
};

export function formatMilliseconds(ms: number): string {
  if (isNaN(ms) || ms < 0) {
    return `${ms}`;
  }

  if (ms < 1000) {
    return `<1s`;
  }

  const dur = dayjs.duration(ms);
  const days = dur.days();
  const hours = dur.hours();
  const minutes = dur.minutes();
  const seconds = dur.seconds();
  const parts = [
    days > 0 && `${days}d`,
    hours > 0 && `${hours}h`,
    minutes > 0 && `${minutes}m`,
    seconds > 0 && `${seconds}s`,
  ].filter(Boolean);

  return parts.slice(0, 2).join(" ") || "0s";
}

export const getInstanceName = (instanceId: string | number, datastores: Datastore[]) => {
  return datastores?.find(store => String(store.instance_id) === String(instanceId))?.name;
};

export const getDbtIntegrationName = (
  id: string,
  integrations?: DbtModelFiltersResponse["dbt_integrations"]
) => {
  return integrations?.find(integration => integration.value === id)?.label;
};

export const getInstanceEdition = (instanceId: string | number, datastores: Datastore[]) => {
  return datastores?.find(store => String(store.instance_id) === String(instanceId))
    ?.instance_edition;
};

export function getInstanceIdByNameFromDataStores(
  dataStore: Array<{ name: string; instance_id?: string | null | undefined }> | undefined,
  instanceName: string | undefined
) {
  return dataStore?.find(i => i.name === instanceName)?.instance_id;
}

/**
 * Safely formats a user's full name, handling null/undefined values
 * @param user - User object with first_name and last_name
 * @param fallback - Optional fallback text (default: "Unknown")
 * @returns Formatted full name or fallback
 */
export function formatUserName(
  user:
    | { first_name?: string | null | undefined; last_name?: string | null | undefined }
    | null
    | undefined,
  fallback: string = "Unknown"
): string {
  if (!user) return fallback;

  const parts = [user.first_name, user.last_name].filter(Boolean);
  return parts.length > 0 ? parts.join(" ").trim() : fallback;
}

/**
 * Safely formats a user's initials for avatar, handling null/undefined values
 * @param user - User object with first_name and last_name
 * @returns Formatted initials or empty string
 */
export function getUserInitials(
  user:
    | { first_name?: string | null | undefined; last_name?: string | null | undefined }
    | null
    | undefined
): string {
  if (!user) return "";

  const firstName = user.first_name?.trim();
  const lastName = user.last_name?.trim();

  return [firstName?.[0]?.toUpperCase(), lastName?.[0]?.toUpperCase()].filter(Boolean).join("");
}

/**
 * Checks if the current viewport width is at or below a mobile breakpoint
 * @returns true if viewport is mobile-sized
 *
 * TODO: Consider also using Navigator.platform or User-Agent for more robust detection
 * https://developer.mozilla.org/en-US/docs/Web/API/Navigator/platform
 */
export const isMobile = (): boolean => window.innerWidth <= MOBILE_SCREEN_BREAKPOINT;

/**
 * Parses an ID to a number and validates it's a positive integer.
 * Returns null if the ID is invalid (NaN, null, undefined, zero, or negative).
 *
 * @param rawId - ID from URL params or other sources
 * @returns Parsed positive number or null
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- rawId can be any type from URL params
export const parseId = (rawId: any): number | null => {
  const parsed = parseInt(rawId, 10);
  const result = parsed > 0 ? parsed : null;
  return result;
};

const PRODUCT_NAME_ACRONYMS = ["DLT", "SQL", "AI", "ML", "CLI"];

export const isAcronym = (word: string): boolean =>
  PRODUCT_NAME_ACRONYMS.includes(word.toUpperCase());

export const formatTitleCase = (str: string): string => {
  return str
    .toLowerCase()
    .split(/[_\s]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const formatPercent = (value: string | number): string => `${Number(value).toFixed(0)}%`;

export const getDaysBetween = (startDate: Date, endDate?: Date): number => {
  const end = endDate || new Date();
  return dayjs(end).diff(dayjs(startDate), "day");
};
